import React from 'react'
import {SortableContainer, SortableElement, SortableHandle} from 'react-sortable-hoc'
import DragIcon from '../../../../../common/icons/DragIcon'
import Icon from '../../../../../common/Icon'
import NodeLayerHeader from './NodeLayerHeader'

const DragHandle = SortableHandle(({locked}) => (
    <span
        className={`SidebarPanel__LayerAction ${locked ? 'SidebarPanel__LayerAction--disabled' : ''}`}
        aria-label="Reorder"
    >
        <Icon icon={DragIcon} width={16} height={16} />
    </span>
))

function NodeListItemContent({node, isSelected, onSelectNode, onRemoveNode, onToggleNodeLock, useDragHandle}) {
    return (
        <NodeLayerHeader
            node={node}
            className="SidebarPanel__LayerItem"
            isSelected={isSelected}
            onSelect={onSelectNode}
            onRemove={onRemoveNode}
            onLockToggle={onToggleNodeLock}
            dragControl={useDragHandle ? <DragHandle locked={!!node.state?.locked} /> : undefined}
        />
    )
}

function SortableNodeRowBase({node, isSelected, onSelectNode, onRemoveNode, onToggleNodeLock, renderNodeDetails}) {
    return (
        <div className={`SidebarPanel__LayerRow ${isSelected ? 'is-selected' : ''}`}>
            <NodeListItemContent
                node={node}
                isSelected={isSelected}
                onSelectNode={onSelectNode}
                onRemoveNode={onRemoveNode}
                onToggleNodeLock={onToggleNodeLock}
                useDragHandle
            />
            {isSelected ? renderNodeDetails?.(node) : null}
        </div>
    )
}

const SortableNodeItem = SortableElement(SortableNodeRowBase)

function SortableNodesListBase({
    nodes,
    selectedNodeIds,
    onSelectNode,
    onRemoveNode,
    onToggleNodeLock,
    renderNodeDetails,
}) {
    const selectedNodeIdSet = new Set(selectedNodeIds)

    return (
        <div className="SidebarPanel__LayersList">
            {nodes.map((node, index) => (
                <SortableNodeItem
                    key={node.id}
                    index={index}
                    node={node}
                    isSelected={selectedNodeIdSet.has(node.id)}
                    onSelectNode={onSelectNode}
                    onRemoveNode={onRemoveNode}
                    onToggleNodeLock={onToggleNodeLock}
                    renderNodeDetails={renderNodeDetails}
                    disabled={!!node.state?.locked}
                />
            ))}
        </div>
    )
}

const SortableList = SortableContainer(SortableNodesListBase)

function NodesList({
    nodes,
    selectedNodeIds = [],
    onSelectNode,
    onRemoveNode,
    onToggleNodeLock,
    onReorder,
    renderNodeDetails,
}) {
    const handleSortEnd = ({oldIndex, newIndex}) => {
        if (oldIndex === newIndex) return
        const nodeId = nodes[oldIndex]?.id
        if (nodeId) onReorder(nodeId, newIndex)
    }

    if (nodes.length === 0) {
        return (
            <div className="SidebarPanel__LayersList">
                <div className="SidebarPanel__Empty">No layers yet</div>
            </div>
        )
    }

    return (
        <SortableList
            nodes={nodes}
            selectedNodeIds={selectedNodeIds}
            onSelectNode={onSelectNode}
            onRemoveNode={onRemoveNode}
            onToggleNodeLock={onToggleNodeLock}
            renderNodeDetails={renderNodeDetails}
            onSortEnd={handleSortEnd}
            lockAxis="y"
            helperClass="SidebarPanel__LayerRow--dragging"
        />
    )
}

export {NodesList, NodeListItemContent as NodeListItem}
